import { CrudQueryBuilder } from "@contember/client";
import { PRIMARY_KEY_NAME } from "../bindingTypes.js";
import { ucfirst } from "../utils/ucfirst.js";
import { EntityListSubTreeMarker } from "../markers/EntityListSubTreeMarker.js";
import { EntitySubTreeMarker } from "../markers/EntitySubTreeMarker.js";
import { assertNever } from "../utils/assertNever.js";
import { FieldMarker } from "../markers/FieldMarker.js";
import { HasOneRelationMarker } from "../markers/HasOneRelationMarker.js";
import { HasManyRelationMarker } from "../markers/HasManyRelationMarker.js";
class QueryGenerator {
  constructor(tree) {
    this.tree = tree;
  }
  getReadQuery() {
    try {
      let baseQueryBuilder = new CrudQueryBuilder.CrudQueryBuilder();
      for (const [, subTreeMarker] of this.tree.subTrees) {
        baseQueryBuilder = this.addSubQuery(subTreeMarker, baseQueryBuilder);
      }
      return baseQueryBuilder.getGql();
    } catch (e) {
      return void 0;
    }
  }
  addSubQuery(subTree, baseQueryBuilder) {
    if (subTree.parameters.isCreating) {
      return baseQueryBuilder;
    }
    if (subTree instanceof EntityListSubTreeMarker) {
      return this.addListQuery(baseQueryBuilder, subTree);
    } else if (subTree instanceof EntitySubTreeMarker) {
      return this.addGetQuery(baseQueryBuilder, subTree);
    }
    assertNever();
  }
  addGetQuery(baseQueryBuilder, subTree) {
    if (subTree.parameters.isCreating) {
      return baseQueryBuilder;
    }
    const populatedListQueryBuilder = QueryGenerator.registerQueryPart(
      subTree.fields.markers,
      CrudQueryBuilder.ReadBuilder.instantiate().by(subTree.parameters.where)
    );
    return baseQueryBuilder.get(
      subTree.entityName,
      CrudQueryBuilder.ReadBuilder.instantiate(
        populatedListQueryBuilder ? populatedListQueryBuilder.objectBuilder : void 0
      ),
      subTree.placeholderName
    );
  }
  addListQuery(baseQueryBuilder, subTree) {
    if (subTree.parameters.isCreating) {
      return baseQueryBuilder;
    }
    let finalBuilder;
    if (subTree.parameters) {
      const parameters = subTree.parameters;
      const withFilter = parameters.filter ? CrudQueryBuilder.ReadBuilder.instantiate().filter(parameters.filter) : CrudQueryBuilder.ReadBuilder.instantiate();
      const withOrderBy = parameters.orderBy ? withFilter.orderBy(parameters.orderBy) : withFilter;
      const withOffset = parameters.offset === void 0 ? withOrderBy : withOrderBy.offset(parameters.offset);
      finalBuilder = parameters.limit === void 0 ? withOffset : withOffset.limit(parameters.limit);
    } else {
      finalBuilder = CrudQueryBuilder.ReadBuilder.instantiate();
    }
    return baseQueryBuilder.list(
      subTree.entityName,
      QueryGenerator.registerQueryPart(subTree.fields.markers, finalBuilder),
      subTree.placeholderName
    );
  }
  static registerQueryPart(fields, builder) {
    builder = builder.column(PRIMARY_KEY_NAME);
    for (const [, fieldValue] of fields) {
      if (fieldValue instanceof FieldMarker) {
        if (fieldValue.fieldName !== PRIMARY_KEY_NAME) {
          builder = builder.column(fieldValue.fieldName);
        }
      } else if (fieldValue instanceof HasOneRelationMarker) {
        const relation = fieldValue.parameters;
        const builderWithBody = CrudQueryBuilder.ReadBuilder.instantiate(
          this.registerQueryPart(fieldValue.fields.markers, CrudQueryBuilder.ReadBuilder.instantiate()).objectBuilder
        );
        const filteredBuilder = relation.filter ? builderWithBody.filter(relation.filter) : builderWithBody;
        if (relation.reducedBy) {
          const relationField = `${relation.field}By${ucfirst(Object.keys(relation.reducedBy)[0])}`;
          builder = builder.reductionRelation(
            relationField,
            filteredBuilder.by(relation.reducedBy),
            fieldValue.placeholderName
          );
        } else {
          builder = builder.hasOneRelation(
            relation.field,
            filteredBuilder,
            fieldValue.placeholderName === relation.field ? void 0 : fieldValue.placeholderName
          );
        }
      } else if (fieldValue instanceof HasManyRelationMarker) {
        const relation = fieldValue.parameters;
        const builderWithBody = CrudQueryBuilder.ReadBuilder.instantiate(
          this.registerQueryPart(fieldValue.fields.markers, CrudQueryBuilder.ReadBuilder.instantiate()).objectBuilder
        );
        const withFilter = relation.filter ? builderWithBody.filter(relation.filter) : builderWithBody;
        const withOrderBy = relation.orderBy ? withFilter.orderBy(relation.orderBy) : withFilter;
        const withOffset = relation.offset === void 0 ? withOrderBy : withOrderBy.offset(relation.offset);
        const withLimit = relation.limit === void 0 ? withOffset : withOffset.limit(relation.limit);
        builder = builder.anyRelation(
          relation.field,
          withLimit,
          fieldValue.placeholderName === relation.field ? void 0 : fieldValue.placeholderName
        );
      } else {
        assertNever();
      }
    }
    return builder;
  }
}
export {
  QueryGenerator
};
//# sourceMappingURL=QueryGenerator.js.map
